// Plan preview shown after the merchant configures their first destination.
//
// Under Shopify App Pricing we don't create charges ourselves — Shopify hosts
// the real plan-selection + approval page. So this screen previews the tiers and
// hands off to that hosted page (opened in the top frame). When the merchant
// returns, Shopify appends ?plan_handle=..., which App.tsx detects to refresh
// status. See docs/APP-PRICING-MIGRATION.md.
//
// Placement rationale (IMPLEMENTATION_PLAN.md §12): we deliberately do NOT show
// this post-install. By the time it renders, the merchant has connected a
// destination and seen events flow, so picking a plan feels like the natural
// next step, not a gate.
//
// Two modes (review W31):
//   "free"   inside the 30 free days a store with no plan gets. "Maybe
//            later" is valid until the end date, which the banner states.
//   "ended"  past it: delivery is paused. App shows this on every session,
//            before onboarding, and the skip ("Not now") exists only when
//            the store has a destination (canSkip).

import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner,
  Box,
  Divider,
} from "@shopify/polaris";
import { openPlanSelection, type FreePeriodView } from "./billing-shared";
import { formatDate } from "./dates";

type PlanName = "flex" | "standard" | "pro";

interface TierMeta {
  plan: PlanName;
  displayName: string;
  priceLabel: string;
  priceSubtitle: string;
  blurb: string;
  highlights: string[];
}

// Monthly-only (annual dropped in the App Pricing migration). Display copy lives
// here; the billing math stays on the backend.
const TIERS: TierMeta[] = [
  {
    plan: "flex",
    displayName: "Flex",
    priceLabel: "$0",
    priceSubtitle: "then $0.25 per order",
    blurb: "Pay only for what you use.",
    highlights: ["No monthly base", "$0.25 per order, billed via Shopify", "Cancel anytime"],
  },
  {
    plan: "standard",
    displayName: "Standard",
    priceLabel: "$129",
    priceSubtitle: "/month",
    blurb: "Best value for most growing stores.",
    highlights: ["1,500 orders/month included", "$0.15 per additional order", "30-day free trial"],
  },
  {
    plan: "pro",
    displayName: "Pro",
    priceLabel: "$299",
    priceSubtitle: "/month",
    blurb: "Built for higher-volume stores.",
    highlights: ["5,000 orders/month included", "$0.07 per additional order", "30-day free trial"],
  },
];

interface PlanPickerProps {
  shop: string;
  // Shopify's hosted plan-selection URL, from GET /api/billing/status.
  planSelectionUrl?: string;
  // Skip → continue to the dashboard without subscribing.
  onSkipped: () => void;
  // "free" (default) or "ended" (review W31); see the header.
  mode?: "free" | "ended";
  // From GET /api/billing/status; states the end date when present.
  freePeriod?: FreePeriodView | null;
  // False hides the skip button (ended with no destination).
  canSkip?: boolean;
}

export function PlanPicker({
  shop,
  planSelectionUrl,
  onSkipped,
  mode = "free",
  freePeriod,
  canSkip = true,
}: PlanPickerProps) {
  const endDate = freePeriod ? formatDate(freePeriod.endsAt) : null;

  return (
    <Page title="Choose a plan" subtitle={`Datacrow for ${shop}`}>
      <BlockStack gap="400">
        {mode === "ended" ? (
          <Banner
            tone="warning"
            title={endDate ? `Your free period ended on ${endDate}` : "Your free period has ended"}
          >
            <p>
              Datacrow still records your store's events but has stopped sending them to
              your ad platforms. Pick a plan to start sending again. Events from the paused
              days are not sent later.
            </p>
          </Banner>
        ) : (
          <Banner tone="success">
            <p>
              Your destination is connected and events are flowing.{" "}
              {endDate
                ? `Datacrow is free until ${endDate}. Pick a plan before then to keep them flowing.`
                : "Pick a plan to keep them flowing."}{" "}
              Standard and Pro include a 30-day free trial, so you won't be charged today.
            </p>
          </Banner>
        )}

        <InlineStack gap="400" align="start" wrap>
          {TIERS.map((tier) => (
            <TierCard key={tier.plan} tier={tier} recommended={tier.plan === "standard"} />
          ))}
        </InlineStack>

        <Box paddingBlockStart="200">
          <InlineStack align="center" gap="300">
            <Button
              variant="primary"
              onClick={() => openPlanSelection(planSelectionUrl)}
              disabled={!planSelectionUrl}
            >
              Choose a plan
            </Button>
            {canSkip && (
              <Button variant="plain" onClick={onSkipped}>
                {mode === "ended" ? "Not now, show my dashboard" : "Maybe later, take me to the dashboard"}
              </Button>
            )}
          </InlineStack>
        </Box>

        <InlineStack align="center">
          <Text as="p" tone="subdued" variant="bodySm">
            Plans are managed securely through Shopify billing.
          </Text>
        </InlineStack>
      </BlockStack>
    </Page>
  );
}

// ---------- Per-tier card (preview only) ----------

function TierCard({ tier, recommended }: { tier: TierMeta; recommended?: boolean }) {
  return (
    <Box minWidth="280px" maxWidth="320px">
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h2" variant="headingMd">
              {tier.displayName}
            </Text>
            {recommended ? (
              <Badge tone="success">Recommended</Badge>
            ) : (
              <Badge tone="info">Optional</Badge>
            )}
          </InlineStack>

          <Text as="p" tone="subdued">
            {tier.blurb}
          </Text>

          <BlockStack gap="050">
            <Text as="p" variant="heading2xl">
              {tier.priceLabel}
            </Text>
            <Text as="p" tone="subdued">
              {tier.priceSubtitle}
            </Text>
          </BlockStack>

          <Divider />

          <BlockStack gap="100">
            {tier.highlights.map((h) => (
              <Text as="p" key={h}>
                • {h}
              </Text>
            ))}
          </BlockStack>
        </BlockStack>
      </Card>
    </Box>
  );
}
